import { styled } from 'styled-components';
import { defaultTheme } from '@/ui/theme';
import { LOAD_LANE_STATUS_CONFIG } from './constants';

export const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 180px);
  min-height: 520px;
  gap: 20px;
`;

export const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  border: 1px solid #e8e8ef;
  overflow: hidden;
  background: ${defaultTheme.white};
`;

export const MainPanel = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  align-items: stretch;
`;

export const SidebarPanel = styled.aside<{ $collapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $collapsed }) => ($collapsed ? '44px' : '280px')};
  flex-shrink: 0;
  min-height: 0;
  overflow: hidden;
  border-left: 1px solid #e8e8ef;
  background: ${defaultTheme.white};
  font-size: 11px;
  transition: width 0.2s ease;
`;

export const SidebarCollapsedStrip = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 12px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${defaultTheme.main};

  &:hover {
    background: ${defaultTheme.defaultGrayBgColor};
  }
`;

export const SidebarCollapsedLabel = styled.span`
  writing-mode: vertical-rl;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${defaultTheme.lightText};
`;

export const SidebarCollapseToggle = styled.button`
  border: none;
  background: transparent;
  color: ${defaultTheme.main};
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    background: ${defaultTheme.defaultGrayBgColor};
  }
`;

export const SidebarTop = styled.div`
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  padding: 8px;
`;

export const DayFilterBar = styled.div`
  position: relative;
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

export const DayFilterButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  height: 36px;
  border-radius: 8px;
  border: 1px solid
    ${({ $active }) => ($active ? defaultTheme.main : '#d5d9e2')};
  background: ${({ $active }) =>
    $active ? defaultTheme.main : defaultTheme.white};
  color: ${({ $active }) => ($active ? defaultTheme.white : defaultTheme.main)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export const DayFilterIconButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid
    ${({ $active }) => ($active ? defaultTheme.main : '#d5d9e2')};
  background: ${({ $active }) =>
    $active ? defaultTheme.main : defaultTheme.white};
  color: ${({ $active }) => ($active ? defaultTheme.white : defaultTheme.main)};
  cursor: pointer;
`;

export const UpcomingCalendar = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 3;
  max-height: calc(100vh - 240px);
  overflow: auto;
  padding: 8px 10px 10px;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  background: ${defaultTheme.white};
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
`;

export const UpcomingCalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  color: ${defaultTheme.main};
  font-size: 14px;
  font-weight: 700;
`;

export const UpcomingCalendarNav = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: ${defaultTheme.main};
  cursor: pointer;
`;

export const UpcomingCalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

export const UpcomingCalendarWeekday = styled.span`
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  color: #8b93a7;
`;

export const UpcomingCalendarDay = styled.button<{
  $outside?: boolean;
  $selected?: boolean;
}>`
  height: 28px;
  border: none;
  border-radius: 8px;
  background: ${({ $selected }) =>
    $selected ? defaultTheme.main : 'transparent'};
  color: ${({ $selected, $outside }) =>
    $selected ? defaultTheme.white : $outside ? '#c5cad3' : defaultTheme.main};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? defaultTheme.main : '#f4f5f7'};
  }
`;

export const ApplyDateButton = styled.button`
  position: sticky;
  bottom: 0;
  width: 100%;
  height: 34px;
  margin-top: 6px;
  border: 1px solid #9ba8c7;
  border-radius: 8px;
  background: ${defaultTheme.white};
  color: ${defaultTheme.main};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f7f8fb;
  }
`;

export const SidebarMiddle = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #dfe1e6;
  border-left: none;
  border-right: none;
  font-size: 11px;
`;

export const SidebarBottom = styled.div`
  flex-shrink: 0;
  padding: 8px;
  font-size: 10px;
`;

export const PageFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const PageTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const PageTitleText = styled.span`
  font-size: 28px;
  font-weight: 600;
  line-height: 28px;
  color: ${defaultTheme.main};
`;

export const PageDate = styled.span`
  font-size: 10px;
  font-weight: normal;
  line-height: 28px;
  color: ${defaultTheme.lightText};
`;

export const SummaryBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-left: auto;
`;

export const SummaryBadge = styled.div<{
  $bg: string;
  $color: string;
  $border: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $border }) => $border};
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8ef;
  background: ${defaultTheme.white};
`;

export const StatusTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const StatusTab = styled.button<{
  $active?: boolean;
  $border?: string;
  $tabBg?: string;
  $color?: string;
}>`
  border: 1px solid
    ${({ $active, $border }) => ($active && $border ? $border : '#DEE2E6')};
  background: ${({ $active, $tabBg }) =>
    $active && $tabBg ? $tabBg : defaultTheme.white};
  color: ${({ $active, $color }) => ($active && $color ? $color : '#5C6B7A')};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

export const TablePanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: ${defaultTheme.white};
`;

export const TableCard = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export const TableScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const TableFooter = styled.div`
  flex-shrink: 0;
  border-top: 1px solid #edf2f7;
  background: ${defaultTheme.white};
`;

export const TableFooterExpanded = styled.div`
  padding: 8px 8px 4px;
  border-bottom: 1px solid #edf2f7;
`;

export const TableFooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 8px;
  font-size: 12px;
  color: ${defaultTheme.lightText};
`;

export const TableFooterStats = styled.span`
  min-width: 0;
`;

export const FooterLaneGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-height: 60px;
  overflow-y: auto;
`;

export const FooterLaneChip = styled.span<{
  $bg: string;
  $color: string;
  $border: string;
  $highlight?: 'added' | 'removed';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 2px 4px;
  border-radius: 0.25rem;
  border: ${({ $highlight, $border }) => {
    if ($highlight === 'removed') return '2px solid #C92A2A';
    if ($highlight === 'added') return '2px solid #166534';
    return `1px solid ${$border}`;
  }};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-size: 11px;
  font-weight: 600;
`;

export const StatusPill = styled.span<{
  $color: string;
  $bg: string;
  $dot: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $dot }) => $dot};
    flex-shrink: 0;
  }
`;

export const CarrierBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  padding: 4px 8px;
  border-radius: 6px;
  background: ${defaultTheme.defaultGrayBgColor};
  font-size: 11px;
  font-weight: 700;
  color: ${defaultTheme.main};
  text-transform: uppercase;
`;

export const VehicleProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const VehicleSquares = styled.div`
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
  max-width: 120px;
`;

export const VehicleSquare = styled.span<{
  $filled?: boolean;
  $color?: string;
}>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid
    ${({ $filled, $color }) =>
      $filled ? $color || defaultTheme.main : '#DEE2E6'};
  background: ${({ $filled, $color }) =>
    $filled ? $color || defaultTheme.main : 'transparent'};
`;

export const VehicleCount = styled.span`
  color: #344563;
  font-weight: 500;
  font-size: 10px;
`;

export const AssignLoadButton = styled.button`
  border: 1px dashed #cbd5e0;
  background: transparent;
  color: ${defaultTheme.main};
  border-radius: 6px;
  padding: 4px 10px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${defaultTheme.defaultGrayBgColor};
  }
`;

export const ExpandedVehicles = styled.div`
  padding: 12px 16px 16px 48px;
  background: #fafbfc;
  border-top: 1px solid #edf2f7;
`;

export const ExpandedTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${defaultTheme.lightText};
  margin-bottom: 12px;
`;

export const VehicleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 24px;
`;

export const VehicleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 11px;
`;

export const VehicleDot = styled.span<{ $pulled?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $pulled }) => ($pulled ? '#38A169' : '#CBD5E0')};
  flex: 0 0 8px;
`;

export const VehiclePlate = styled.span<{
  $newAdded: boolean;
  $removed: boolean;
}>`
  display: block;
  width: 100%;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $newAdded, $removed }) =>
    $newAdded ? '#166534' : $removed ? '#C53030' : 'inherit'};
  text-decoration: ${({ $removed }) => ($removed ? 'line-through' : 'none')};
`;

export const VehiclePlateCell = styled.span`
  flex: 0 0 150px;
  width: 150px;
  overflow: hidden;
`;

export const VehicleDescriptionCell = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`;

export const VehicleDescription = styled.span<{
  $newAdded: boolean;
  $removed: boolean;
}>`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $newAdded, $removed }) =>
    $newAdded ? '#166534' : $removed ? '#C53030' : defaultTheme.lightText};
  text-decoration: ${({ $removed }) => ($removed ? 'line-through' : 'none')};
`;

export const VehicleStatusCell = styled.span`
  flex: 0 0 52px;
  width: 52px;
  overflow: hidden;
`;

export const VehicleStatus = styled.span<{ $pulled?: boolean }>`
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  color: ${({ $pulled }) => ($pulled ? '#166534' : defaultTheme.lightText)};
`;

export const VehicleMeta = styled.span<{ $active?: boolean }>`
  color: ${({ $active }) => ($active ? '#166534' : defaultTheme.lightText)};
`;

export const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const SidebarHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const SidebarTitle = styled.h2`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${defaultTheme.main};
`;

export const PendingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border-radius: 0.25rem;
  background: ${defaultTheme.defaultGrayBgColor};
  font-size: 10px;
  font-weight: 500;
  color: ${defaultTheme.textColor};
`;

export const SidebarSectionTitle = styled.div`
  margin-bottom: 10px;
  font-weight: 500;
  color: ${defaultTheme.lightText};
`;

export const LaneGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-height: 60px;
  overflow-y: auto;
`;

export const LaneChip = styled.button`
  min-width: 28px;
  padding: 2px 4px;
  border-radius: 0.25rem;
  border: 1px solid #dfe1e6;
  background: #f4f5f7;
  color: ${defaultTheme.lightText};
`;

export const EmptyState = styled.div`
  padding: 6px;
  text-align: center;
  color: ${defaultTheme.lightText};
`;

export const RefreshButton = styled.button`
  border: none;
  background: transparent;
  color: ${defaultTheme.main};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;

  &:hover {
    background: ${defaultTheme.defaultGrayBgColor};
  }
`;

export const DriverCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${defaultTheme.textColor};
`;

export const OverdueText = styled.span`
  color: ${LOAD_LANE_STATUS_CONFIG.overdue.color};
  font-weight: 600;
  font-size: 13px;
`;

export const SidebarAssignBanner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 6px;
  margin-bottom: 8px;
  background: ${defaultTheme.main};
  color: ${defaultTheme.white};
`;

export const SidebarAssignBannerTitle = styled.div`
  font-weight: 500;
  line-height: 1.3;
`;

export const CloseSidebarButton = styled.button<{ $light?: boolean }>`
  border: none;
  background: transparent;
  color: ${({ $light }) => ($light ? defaultTheme.white : defaultTheme.gray)};
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
`;

export const UpcomingLoadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 12px;
`;

export const UpcomingLoadCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  background: ${defaultTheme.white};
`;

export const UpcomingLoadHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

export const UpcomingLoadRef = styled.span`
  font-weight: 500;
  color: ${defaultTheme.main};
`;

export const UpcomingLoadMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${defaultTheme.lightText};
  margin-bottom: 12px;
`;

export const UpcomingLoadMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const AssignToLaneButton = styled.button<{
  $variant?: 'primary' | 'secondary';
}>`
  width: 100%;
  border-radius: 6px;
  background: ${({ $variant }) =>
    $variant === 'primary' ? defaultTheme.main : 'white'};
  border: 1px solid
    ${({ $variant }) => ($variant === 'primary' ? 'transparent' : '#9ba8c7')};
  color: ${({ $variant }) =>
    $variant === 'primary' ? 'white' : defaultTheme.main};
  padding: 4px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SelectVacantLaneTitle = styled.div`
  color: ${defaultTheme.main};
`;

export const LaneSelectGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`;

export const LaneSelectButton = styled.button`
  padding: 4px;
  border-radius: 0.25rem;
  border: 1px solid #9ba8c7;
  background: ${defaultTheme.white};
  color: ${defaultTheme.main};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${defaultTheme.main};
    color: ${defaultTheme.white};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const LaneSelectCancel = styled.button`
  border: none;
  background: transparent;
  color: ${defaultTheme.lightText};
  cursor: pointer;
  padding: 0;
`;

export const LaneCell = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding-right: 14px;
  font-weight: 600;
`;

export const LaneLoadBadge = styled.span`
  position: absolute;
  top: -4px;
  right: 0;
  padding: 0 3px;
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  text-align: left;
`;

export const UpcomingEtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 0;
  color: inherit;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;
